import React, { useRef, useEffect, useState } from 'react';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
} from '@mui/icons-material';

const RichTextEditor = ({ value, onChange, placeholder, minHeight = '300px' }) => {
  const editorRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [formatType, setFormatType] = useState(null);
  const [currentFormat, setCurrentFormat] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
  });

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (html) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  };

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateFormatState();
  };

  const handleInput = () => {
    let content = editorRef.current?.innerHTML || '';
    // Decode HTML entities so that typed HTML tags are sent as proper HTML
    content = decodeHtmlEntities(content);
    onChange(content);
    updateFormatState();
  };

  const updateFormatState = () => {
    setCurrentFormat({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
    });
  };

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setFormatType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setFormatType(null);
  };

  const handleFormatBlock = (tag) => {
    handleFormat('formatBlock', tag);
    handleMenuClose();
  };

  const handleFontName = (font) => {
    handleFormat('fontName', font);
    handleMenuClose();
  };

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      handleFormat('createLink', url);
    }
  };

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <Button
      size="small"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      sx={{
        minWidth: '32px',
        padding: '4px 8px',
        color: active ? '#4E83BD' : '#2C3E50',
        backgroundColor: active ? '#E8F4F8' : 'transparent',
        '&:hover': {
          backgroundColor: '#ECF0F1',
          color: '#4E83BD',
        },
      }}
      title={title}
    >
      {children}
    </Button>
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          marginBottom: 0,
          paddingBottom: 1,
          paddingTop: 1,
          paddingX: 1,
          borderBottom: '1px solid #ECF0F1',
          backgroundColor: '#FFFFFF',
          borderRadius: '4px 4px 0 0',
          border: '1px solid #ECF0F1',
          borderBottom: '1px solid #ECF0F1',
          flexWrap: 'wrap',
        }}
      >
        <ToolbarButton
          onClick={() => handleFormat('bold')}
          active={currentFormat.bold}
          title="Bold"
        >
          <FormatBold fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => handleFormat('italic')}
          active={currentFormat.italic}
          title="Italic"
        >
          <FormatItalic fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => handleFormat('underline')}
          active={currentFormat.underline}
          title="Underline"
        >
          <FormatUnderlined fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => handleFormat('strikeThrough')}
          active={currentFormat.strikeThrough}
          title="Strikethrough"
        >
          <FormatStrikethrough fontSize="small" />
        </ToolbarButton>

        <Box sx={{ width: '1px', backgroundColor: '#ECF0F1', marginX: 0.5 }} />

        <Button
          size="small"
          onMouseDown={(e) => {
            e.preventDefault();
            handleMenuOpen(e, 'header');
          }}
          sx={{
            color: '#2C3E50',
            textTransform: 'none',
            padding: '4px 12px',
            '&:hover': {
              backgroundColor: '#ECF0F1',
              color: '#4E83BD',
            },
          }}
        >
          Normal ▼
        </Button>

        <Button
          size="small"
          onMouseDown={(e) => {
            e.preventDefault();
            handleMenuOpen(e, 'font');
          }}
          sx={{
            color: '#2C3E50',
            textTransform: 'none',
            padding: '4px 12px',
            '&:hover': {
              backgroundColor: '#ECF0F1',
              color: '#4E83BD',
            },
          }}
        >
          Font ▼
        </Button>

        <Box sx={{ width: '1px', backgroundColor: '#ECF0F1', marginX: 0.5 }} />

        <ToolbarButton
          onClick={() => handleFormat('insertUnorderedList')}
          title="Bullet List"
        >
          <FormatListBulleted fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => handleFormat('insertOrderedList')}
          title="Numbered List"
        >
          <FormatListNumbered fontSize="small" />
        </ToolbarButton>

        <ToolbarButton onClick={handleLink} title="Insert Link">
          <LinkIcon fontSize="small" />
        </ToolbarButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && formatType === 'header'}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleFormatBlock('p')}>Normal</MenuItem>
        <MenuItem onClick={() => handleFormatBlock('h1')}>Heading 1</MenuItem>
        <MenuItem onClick={() => handleFormatBlock('h2')}>Heading 2</MenuItem>
        <MenuItem onClick={() => handleFormatBlock('h3')}>Heading 3</MenuItem>
      </Menu>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && formatType === 'font'}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleFontName('Arial')}>Arial</MenuItem>
        <MenuItem onClick={() => handleFontName('Georgia')}>Georgia</MenuItem>
        <MenuItem onClick={() => handleFontName('Times New Roman')}>Times New Roman</MenuItem>
        <MenuItem onClick={() => handleFontName('Courier New')}>Courier New</MenuItem>
        <MenuItem onClick={() => handleFontName('Verdana')}>Verdana</MenuItem>
      </Menu>

      <Box
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onMouseUp={updateFormatState}
        onKeyUp={updateFormatState}
        suppressContentEditableWarning
        sx={{
          minHeight: minHeight,
          padding: '12px 16px',
          backgroundColor: '#F8FAFC',
          border: '1px solid #ECF0F1',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          color: '#2C3E50',
          fontSize: '14px',
          lineHeight: '1.6',
          outline: 'none',
          overflowY: 'auto',
          '&:focus': {
            borderColor: '#4E83BD',
          },
          '&:empty:before': {
            content: `"${placeholder}"`,
            color: '#94a3b8',
            pointerEvents: 'none',
          },
          '& p': {
            margin: '0 0 0.5em 0',
          },
          '& p:last-child': {
            marginBottom: 0,
          },
          '& h1, & h2, & h3': {
            margin: '0.5em 0',
            fontWeight: 600,
          },
          '& h1': {
            fontSize: '2em',
          },
          '& h2': {
            fontSize: '1.5em',
          },
          '& h3': {
            fontSize: '1.25em',
          },
          '& ul, & ol': {
            paddingLeft: '2em',
            margin: '0.5em 0',
          },
          '& li': {
            marginBottom: '0.25em',
          },
          '& a': {
            color: '#4E83BD',
            textDecoration: 'underline',
          },
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
